import { StationApi } from 'api/StationApi'
import { ReactComponent as ArrowSvg } from 'images/icons/arrow_select.svg'
import { Fragment, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectStation } from 'store/selectors/station'
import { IStationTelemetryLot, IStationTelemetrySnapshot } from 'utils/interfaces'
import { lotModelDisplayLabel } from 'utils/stationLotModel'
import {
  classifyEquipmentUiGroup,
  equipmentFieldLabel,
  formatEquipmentValue,
  groupEquipmentEntries,
  type EquipmentUiGroup,
} from 'utils/stationEquipment'
import { getStationFeedbackStatus } from 'utils/stationFeedback'

import style from './StationEquipmentSection.module.css'

const POLL_INTERVAL_MS = 5000

const UI_GROUP_CLASS: Record<EquipmentUiGroup, string | undefined> = {
  ok: style.valueOk,
  idle: style.valueIdle,
  active: style.valueActive,
  fault: style.valueFault,
  unknown: style.valueUnknown,
  actuator_fault: style.valueActuatorFault,
  neutral: undefined,
}

function EquipmentBlock({
  lot,
  equipment,
}: {
  lot: IStationTelemetryLot['lot']
  equipment: NonNullable<IStationTelemetryLot['equipment']>
}) {
  const groups = groupEquipmentEntries(equipment, lot.lot_model ?? lot.type)
  if (groups.length === 0) {
    return null
  }
  const lotLabel = lot.lot_model
    ? lotModelDisplayLabel(lot.lot_model)
    : lot.type != null
      ? String(lot.type)
      : 'Unknown'
  return (
    <div className={style.equipment}>
      <div className={style.equipmentTitle}>
        Lot {lot.id} · {lotLabel}
      </div>
      {groups.map(group => (
        <div key={group.id} className={style.equipmentGroup}>
          {groups.length > 1 ? (
            <div className={style.equipmentGroupTitle}>{group.title}</div>
          ) : null}
          <div className={style.equipmentGrid}>
            {group.entries.map(([key, value]) => {
              const uiGroup = classifyEquipmentUiGroup(key, value)
              const valueClass = [style.equipmentValue, UI_GROUP_CLASS[uiGroup]]
                .filter(Boolean)
                .join(' ')
              return (
                <Fragment key={key}>
                  <div className={style.equipmentLabel}>{equipmentFieldLabel(key)}</div>
                  <div className={valueClass} data-ui-group={uiGroup}>
                    {formatEquipmentValue(value)}
                  </div>
                </Fragment>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

const StationEquipmentSection = () => {
  const station = useSelector(selectStation)
  const feedback = getStationFeedbackStatus(station)
  const [expanded, setExpanded] = useState(false)
  const [snapshot, setSnapshot] = useState<IStationTelemetrySnapshot['snapshot']>(null)

  useEffect(() => {
    let cancelled = false

    if (feedback === 'no_feedback') {
      setSnapshot(null)
      return
    }

    const load = async () => {
      try {
        const response = await StationApi.fetchTelemetrySnapshot(station.id)
        if (!cancelled) {
          setSnapshot(response.snapshot)
        }
      } catch (error) {
        console.warn('[StationEquipmentSection] fetch failed', error)
      }
    }

    load()
    const timer = window.setInterval(load, POLL_INTERVAL_MS)
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [station.id, feedback])

  if (feedback === 'no_feedback') {
    return null
  }

  const lotsWithEquipment =
    snapshot?.lots.filter(lot => lot.equipment && Object.keys(lot.equipment).length > 0) ?? []

  return (
    <div className={style.root}>
      <div className={style.header}>
        <div>Equipment</div>
        <div
          className={style.showMore}
          onClick={() => setExpanded(prev => !prev)}
          role="button"
          tabIndex={0}
          onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              setExpanded(prev => !prev)
            }
          }}
          aria-expanded={expanded}
        >
          {expanded ? 'Hide' : 'Show'}
          <ArrowSvg className={style.icon} />
        </div>
      </div>
      {expanded && (
        <div className={style.body}>
          {lotsWithEquipment.length === 0 ? (
            <div className={style.empty}>No equipment telemetry yet.</div>
          ) : (
            lotsWithEquipment.map(lot => (
              <EquipmentBlock key={lot.lot.id} lot={lot.lot} equipment={lot.equipment!} />
            ))
          )}
        </div>
      )}
    </div>
  )
}

export default StationEquipmentSection
